var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link, useHistory } from 'react-router-dom';
import { Accordion, AccordionSet, Button, KeyValue, Layout, Loading, MessageBanner, Pane, PaneMenu, Select, TextArea, } from '@folio/stripes/components';
import { useChangeErrorReportStatus, useErrorReportDetail } from '../../hooks/useErrorReports';
import useUserNames from '../../hooks/useUserNames';
import { isTimeoutError } from '../../utils/errorKind';
/** Static ids, so the translation-integrity test sees every one of them. */
export var ERROR_REPORT_STATUS_LABEL = {
    NEW: 'ui-czna-authorities.errorReports.status.NEW',
    IN_PROGRESS: 'ui-czna-authorities.errorReports.status.IN_PROGRESS',
    RESOLVED: 'ui-czna-authorities.errorReports.status.RESOLVED',
    DISMISSED: 'ui-czna-authorities.errorReports.status.DISMISSED',
};
var AUTHORITIES_PATH = '/czna-authorities/authorities';
var PROPOSE_PATH = '/czna-authorities/proposals/new';
var httpStatus = function (error) { var _a; return (_a = error === null || error === void 0 ? void 0 : error.response) === null || _a === void 0 ? void 0 : _a.status; };
var StatusHistory = function (_a) {
    var items = _a.items;
    var intl = useIntl();
    var getUserName = useUserNames(items.map(function (change) { return change.changedBy; })).getUserName;
    if (items.length === 0) {
        return React.createElement("p", null,
            React.createElement(FormattedMessage, { id: "ui-czna-authorities.errorReports.history.empty" }));
    }
    return (React.createElement("ol", { style: { paddingLeft: '18px', margin: 0 } }, items.map(function (change, index) {
        var _a;
        return (React.createElement("li", { 
            // History is append-only and never reordered, so the position is a stable key.
            // eslint-disable-next-line react/no-array-index-key
            key: index, "data-testid": "error-report-history-".concat(index), style: { marginBottom: '10px' } },
            React.createElement("div", null, "".concat(intl.formatDate(change.changedAt), " ").concat(intl.formatTime(change.changedAt)),
                ' · ', (_a = getUserName(change.changedBy)) !== null && _a !== void 0 ? _a : change.changedBy),
            React.createElement("div", { style: { fontWeight: 600 } },
                React.createElement(FormattedMessage, { id: ERROR_REPORT_STATUS_LABEL[change.fromStatus] }),
                ' → ',
                React.createElement(FormattedMessage, { id: ERROR_REPORT_STATUS_LABEL[change.toStatus] })),
            React.createElement("div", { style: { whiteSpace: 'pre-wrap' } }, change.comment)));
    })));
};
/**
 * The status change. Offers only what the backend allows from the current status
 * (`allowedTransitions`), and will not send a change without a comment — NK decided every
 * change says why; the backend refuses a blank one as well.
 */
var StatusChangeForm = function (_a) {
    var report = _a.report;
    var intl = useIntl();
    var _b = useState(''), target = _b[0], setTarget = _b[1];
    var _c = useState(''), comment = _c[0], setComment = _c[1];
    var _d = useState(null), errorId = _d[0], setErrorId = _d[1];
    var change = useChangeErrorReportStatus(report.id);
    // Another report, or this one after a change: start from a clean form.
    useEffect(function () {
        setTarget('');
        setComment('');
        setErrorId(null);
    }, [report.id, report.status]);
    if (report.allowedTransitions.length === 0)
        return null;
    var canSave = target !== '' && comment.trim() !== '' && !change.isLoading;
    var save = function () {
        if (!canSave)
            return;
        setErrorId(null);
        change.mutate({ status: target, comment: comment.trim() }, {
            onError: function (error) {
                var status = httpStatus(error);
                // CZNA-1625 — no answer in time: the change may have been recorded.
                if (isTimeoutError(error)) {
                    setErrorId('ui-czna-authorities.error.timeout');
                    return;
                }
                setErrorId(status === 409
                    ? 'ui-czna-authorities.errorReports.change.conflict'
                    : status === 403
                        ? 'ui-czna-authorities.errorReports.change.forbidden'
                        : 'ui-czna-authorities.errorReports.change.error');
            },
        });
    };
    return (React.createElement("div", { "data-testid": "error-report-status-change" },
        React.createElement(Select, { label: intl.formatMessage({ id: 'ui-czna-authorities.errorReports.change.status' }), value: target, placeholder: intl.formatMessage({ id: 'ui-czna-authorities.errorReports.change.choose' }), onChange: function (e) {
                return setTarget(e.target.value);
            }, dataOptions: report.allowedTransitions.map(function (value) { return ({
                value: value,
                label: intl.formatMessage({ id: ERROR_REPORT_STATUS_LABEL[value] }),
            }); }) }),
        React.createElement(TextArea, { label: intl.formatMessage({ id: 'ui-czna-authorities.errorReports.change.comment' }), name: "errorReportComment", value: comment, required: true, onChange: function (e) { return setComment(e.target.value); } }),
        errorId && (React.createElement(MessageBanner, { type: "error" },
            React.createElement(FormattedMessage, { id: errorId }))),
        React.createElement(Button, { buttonStyle: "primary", disabled: !canSave, onClick: save },
            React.createElement(FormattedMessage, { id: "ui-czna-authorities.errorReports.change.save" }))));
};
/**
 * CZNA-1596 (UC PC-08) — one public error report: the heading as the reporter saw it, a link
 * to the record as it is now, what they wrote, the source they cite (CZNA-1597; a dash on a
 * report older than that), who they are, and every status change.
 *
 * <p>"Navrhnout opravu" opens a change proposal of the reported record (AR-05h) — the fix goes
 * the ordinary proposal way, never as a direct edit from here.
 */
var ErrorReportDetailPane = function (_a) {
    var _b, _c, _d, _e;
    var id = _a.id, onClose = _a.onClose, onFilterByRecord = _a.onFilterByRecord;
    var intl = useIntl();
    var history = useHistory();
    var _f = useErrorReportDetail(id), report = _f.report, isLoading = _f.isLoading, isError = _f.isError, error = _f.error;
    var paneProps = {
        defaultWidth: '40%',
        paneTitle: (_b = report === null || report === void 0 ? void 0 : report.recordHeading) !== null && _b !== void 0 ? _b : intl.formatMessage({ id: 'ui-czna-authorities.errorReports.detail.title' }),
        dismissible: true,
        onClose: onClose,
    };
    if (isLoading) {
        return React.createElement(Pane, __assign({}, paneProps),
            React.createElement(Loading, null));
    }
    if (isError || !report) {
        var status_1 = httpStatus(error);
        var messageId = status_1 === 403
            ? 'ui-czna-authorities.errorReports.detail.forbidden'
            : status_1 === 404
                ? 'ui-czna-authorities.errorReports.detail.notFound'
                : 'ui-czna-authorities.errorReports.detail.loadError';
        return (React.createElement(Pane, __assign({}, paneProps),
            React.createElement(Layout, { className: "padding-all-gutter" },
                React.createElement(FormattedMessage, { id: messageId }))));
    }
    var recordExists = Boolean(report.recordAuthorityId);
    var actions = (React.createElement(PaneMenu, null, recordExists && (React.createElement(Button, { marginBottom0: true, onClick: function () { return history.push("".concat(PROPOSE_PATH, "?targetRecordId=").concat(encodeURIComponent(report.recordId))); } },
        React.createElement(FormattedMessage, { id: "ui-czna-authorities.errorReports.proposeFix" })))));
    return (React.createElement(Pane, __assign({}, paneProps, { lastMenu: actions }),
        React.createElement("div", { "data-testid": "error-report-detail" },
            React.createElement(KeyValue, { label: React.createElement(FormattedMessage, { id: "ui-czna-authorities.errorReports.detail.recordHeading" }), value: (_c = report.recordHeading) !== null && _c !== void 0 ? _c : '—' }),
            React.createElement(KeyValue, { label: React.createElement(FormattedMessage, { id: "ui-czna-authorities.errorReports.detail.record" }), value: recordExists ? (React.createElement(React.Fragment, null,
                    React.createElement(Link, { to: "".concat(AUTHORITIES_PATH, "/").concat(report.recordId) }, report.recordAuthorityId),
                    ' ',
                    React.createElement(Button, { buttonStyle: "link", marginBottom0: true, onClick: function () { return onFilterByRecord(report.recordId); } },
                        React.createElement(FormattedMessage, { id: "ui-czna-authorities.errorReports.filterByRecord" })))) : React.createElement(FormattedMessage, { id: "ui-czna-authorities.errorReports.detail.recordMissing" }) }),
            React.createElement(KeyValue, { label: React.createElement(FormattedMessage, { id: "ui-czna-authorities.errorReports.detail.status" }), value: React.createElement(FormattedMessage, { id: ERROR_REPORT_STATUS_LABEL[report.status] }) }),
            React.createElement(KeyValue, { label: React.createElement(FormattedMessage, { id: "ui-czna-authorities.errorReports.detail.createdAt" }), value: "".concat(intl.formatDate(report.createdAt), " ").concat(intl.formatTime(report.createdAt)) }),
            React.createElement(KeyValue, { label: React.createElement(FormattedMessage, { id: "ui-czna-authorities.errorReports.detail.description" }), value: React.createElement("div", { style: { whiteSpace: 'pre-wrap' } }, report.description) }),
            React.createElement(KeyValue, { label: React.createElement(FormattedMessage, { id: "ui-czna-authorities.errorReports.detail.source" }), value: (React.createElement("div", { "data-testid": "error-report-source", style: { whiteSpace: 'pre-wrap' } }, (_d = report.source) !== null && _d !== void 0 ? _d : '—')) }),
            React.createElement(KeyValue, { label: React.createElement(FormattedMessage, { id: "ui-czna-authorities.errorReports.detail.reporterEmail" }), value: React.createElement("span", { "data-testid": "error-report-reporter-email" }, (_e = report.reporterEmail) !== null && _e !== void 0 ? _e : '—') }),
            React.createElement(AccordionSet, null,
                React.createElement(Accordion, { label: React.createElement(FormattedMessage, { id: "ui-czna-authorities.errorReports.change.title" }) },
                    React.createElement(StatusChangeForm, { report: report })),
                React.createElement(Accordion, { label: React.createElement(FormattedMessage, { id: "ui-czna-authorities.errorReports.history.title" }) },
                    React.createElement(StatusHistory, { items: report.history }))))));
};
export default ErrorReportDetailPane;
